import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import {
    Login,
    Signup,
    EmailVerification,
    ForgotPassword,
    Intro,
    Home,
} from './pages';
import ProtectedRoute from './components/ProtectedRoute';
import { ToastProvider } from './components/Toast';
import OfflineBanner from './components/OfflineBanner';
import { ThemeProvider } from './contexts/ThemeContext';
import { useWebSocket } from './hooks/useWebSocket';
import Account from './pages/Account';
import { instrumentsService } from './services/background/InstrumentsService';
import { useAppSelector } from './store/hooks';
import { selectSettings } from './store/settingsSlice';

// Component inside Router context to use navigation
function AppRoutes() {
    // Initialize WebSocket connection (connects when user is authenticated)
    useWebSocket();

    // Watch for data provider connections
    const { dataFeedProviders } = useAppSelector(selectSettings);

    // Initialize IndexedDB on mount
    useEffect(() => {
        console.log('[App] Initializing IndexedDB...');

        // Initialize instruments background service (IndexedDB only)
        instrumentsService.init().catch(err => {
            console.error('[App] Failed to initialize instruments service:', err);
        });

        // Cleanup on unmount
        return () => {
            instrumentsService.destroy();
        };
    }, []);

    // Fetch instruments when data provider is connected
    useEffect(() => {
        const connectedProviders = dataFeedProviders.filter(p => p.status === 'connected');

        if (connectedProviders.length > 0) {
            console.log('[App] Data provider connected, triggering instruments fetch...');
            instrumentsService.onDataProviderConnected().catch(err => {
                console.error('[App] Failed to fetch instruments on provider connection:', err);
            });
        }
    }, [dataFeedProviders]);

    return (
        <Routes>
            <Route path="/" element={<Intro />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
                path="/account"
                element={
                    <ProtectedRoute>
                        <Account />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/home"
                element={
                    <ProtectedRoute>
                        <Home />
                    </ProtectedRoute>
                }
            />
        </Routes>
    );
}

function App() {
    return (
        <ThemeProvider>
            <ToastProvider>
                <OfflineBanner />
                <Router>
                    <AppRoutes />
                </Router>
            </ToastProvider>
        </ThemeProvider>
    );
}

export default App;
